import React, { useState, useEffect } from "react"
import moment from "moment"
import CircularProgress from "@mui/material/CircularProgress"
import Typography from "@mui/material/Typography"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import TextField from "@mui/material/TextField"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import Autocomplete from "@mui/material/Autocomplete"
import { useSnackbar } from "notistack"
import { useForm } from "react-hook-form"
import { getMemberOrders } from "services/order"
import { getSubscriptionProducts } from "services/subscription"
import { getProducts as getProductGates } from "services/products"
import {
  cancelKeepSubscription,
  reactivateKeepSubscription,
  getMemberAccess,
  assignTier,
  removeAccess,
} from "services/member"
import { getSimpleProgramList } from "services/program"
import { verifyResponse } from "utils/httpRequest"
import { UseConfirmDialog } from "components/customHooks"
import {
  DURATION_UNIT_LIST,
  ORDER_STATUS,
  PAYMENT_TYPE,
  PAYMENT_TYPE_INDEX,
  SUBSCRIPTION_STATUS,
} from "constants/global"
import { getTierPrograms } from "services/tierProgram"
import { PERMISSIONS } from "constants/permission"
import { UsePermissionVerification } from "components/customHooks"
import { StyledMemberSubscriptions } from "./Member.style"

const MemberSubscriptions = ({
  memberId,
  vipInfoId,
  payment,
  subscriptionStatus,
}) => {
  const [subscriptions, setSubscriptions] = useState([])
  const [products, setProducts] = useState([])
  const [accessList, setAccessList] = useState([])
  const [programs, setPrograms] = useState([])
  const [tierPrograms, setTierPrograms] = useState([])
  const [tierProgramsLoading, setTierProgramsLoading] = useState(true)
  const [tierProgramsError, setTierProgramsError] = useState(null)
  const [productGates, setProductGates] = useState([])
  const [productGatesLoading, setProductGatesLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [tierDialogOpen, setTierDialogOpen] = useState(false)
  const [programDialogOpen, setProgramDialogOpen] = useState(false)
  const [selectedProgram, setSelectedProgram] = useState(null)
  const { enqueueSnackbar } = useSnackbar()

  const AccessControl = UsePermissionVerification()

  const [ConfirmDialog, openConfirmDialog] = UseConfirmDialog({
    title: "Remove Access",
    description: "Are you sure you want to remove this access?",
    agreeText: "Yes, Remove",
    disagreeText: "Cancel",
  })

  const {
    register: registerTier,
    handleSubmit: handleSubmitTier,
    reset: resetTier,
    watch: watchTier,
    setValue: setValueTier,
    formState: { errors: errorsTier },
  } = useForm()

  const {
    register: registerProgram,
    handleSubmit: handleSubmitProgram,
    reset: resetProgram,
    setValue: setValue,
    formState: { errors: errorsProgram },
  } = useForm()

  const loadMemberSubscription = async () => {
    setLoading(true)
    try {
      const response = await getMemberOrders(memberId)
      verifyResponse(response)
      setSubscriptions(response.data.list || [])
    } finally {
      setLoading(false)
    }
  }

  const loadSubscriptionProduct = async () => {
    setLoading(true)
    try {
      const response = await getSubscriptionProducts()
      verifyResponse(response)
      setProducts(response.data || [])
    } finally {
      setLoading(false)
    }
  }

  const loadMemberAccess = async () => {
    try {
      const response = await getMemberAccess({ memberId })
      verifyResponse(response)
      setAccessList(response.data.accessList || [])
    } catch (error) {
      enqueueSnackbar(`Failed to load member access: ${error.message || error}`, {
        variant: "error",
      })
    }
  }

  const loadPrograms = async () => {
    try {
      const response = await getSimpleProgramList()
      verifyResponse(response)
      const programsList = response.data.data?.programs || []
      setPrograms(programsList)
    } catch {
      setPrograms([])
    }
  }

  const loadProductGates = async () => {
    try {
      setProductGatesLoading(true)
      const arr = await getProductGates()
      setProductGates(Array.isArray(arr) ? arr : [])
    } catch {
      setProductGates([])
    } finally {
      setProductGatesLoading(false)
    }
  }

  const getProductByCode = (code) => {
    return products.find((product) => product.code === code)
  }

  const getProductGateByAccessValue = (accessValue) => {
    const productGateId = Number(accessValue || 0)
    if (!Number.isFinite(productGateId) || productGateId <= 0) return null
    return productGates.find((gate) => {
      const ids = [gate.productGateId, gate.programId, gate.id]
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0)
      return ids.includes(productGateId)
    })
  }

  const cancelSubscription = async () => {
    setLoading(true)
    try {
      const response = await cancelKeepSubscription({ vipInfoId, memberId })
      verifyResponse(response)
      await loadSubscriptionProduct()
    } finally {
      setLoading(false)
    }
  }

  const reactivateSubscription = async () => {
    setLoading(true)
    try {
      const response = await reactivateKeepSubscription({ vipInfoId, memberId })
      verifyResponse(response)
      await loadSubscriptionProduct()
    } finally {
      setLoading(false)
    }
  }

  const handleAssignTier = async (data) => {
    setLoading(true)
    try {
      const payload = {
        memberId: parseInt(memberId, 10),
        tierType: data.tierType,
      }

      if (String(data?.tierType || "").toLowerCase() === "product") {
        const pid = data?.programId
        if (!pid) {
          enqueueSnackbar("Product Gate is required when Tier Type is Product.", {
            variant: "error",
          })
          return
        }
        payload.programId = parseInt(pid, 10)
      }
      
      if (data.trialDays && data.trialDays > 0) {
        payload.trialDays = parseInt(data.trialDays, 10)
      }

      const response = await assignTier(payload)
      verifyResponse(response)
      
      enqueueSnackbar("Tier assigned successfully", {
        variant: "success",
      })
      
      setTierDialogOpen(false)
      resetTier()
      loadMemberAccess()
    } catch (error) {
      enqueueSnackbar(`Failed to assign tier: ${error.message || error}`, {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveAccess = async (access) => {
    openConfirmDialog(() => {
      setLoading(true)
      const payload = { memberAccessId: access.id }
      
      removeAccess(payload)
        .then((response) => {
          verifyResponse(response)
          
          enqueueSnackbar("Access removed successfully", {
            variant: "success",
          })
          
          loadMemberAccess()
        })
        .catch((error) => {
          enqueueSnackbar(`Failed to remove access: ${error.message || error}`, {
            variant: "error",
          })
        })
        .finally(() => {
          setLoading(false)
        })
    })
  }

  const handleAssignProgram = async (data) => {
    setLoading(true)
    try {
      const payload = {
        memberId: parseInt(memberId, 10),
        programId: parseInt(data.programId, 10),
      }

      const response = await assignTier(payload)
      verifyResponse(response)
      
      enqueueSnackbar("Program assigned successfully", {
        variant: "success",
      })
      
      setProgramDialogOpen(false)
      resetProgram()
      setSelectedProgram(null)
      loadMemberAccess() // Refresh access list to show new program assignment
    } catch {
      enqueueSnackbar("Failed to assign program", {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSubscriptionProduct()
    loadMemberSubscription()
    loadMemberAccess()
    loadPrograms()
    loadProductGates()
  }, [memberId])

  // Load tier programs on component mount
  useEffect(() => {
    const loadTierPrograms = async () => {
      try {
        setTierProgramsLoading(true)
        setTierProgramsError(null)
        const response = await getTierPrograms()
        const programs = response.data || response
        if (Array.isArray(programs)) {
          setTierPrograms(programs)
        } else {
          throw new Error("Tier programs API returned non-array data")
        }
      } catch (error) {
        setTierProgramsError(error.message)
        enqueueSnackbar("Failed to load tier programs", { variant: "error" })
      } finally {
        setTierProgramsLoading(false)
      }
    }
    
    loadTierPrograms()
  }, [enqueueSnackbar])

  // Refresh product gates when the tier dialog opens (needed for Tier Type = product)
  useEffect(() => {
    if (!tierDialogOpen) return
    let cancelled = false
    ;(async () => {
      try {
        setProductGatesLoading(true)
        const arr = await getProductGates()
        if (!cancelled) setProductGates(Array.isArray(arr) ? arr : [])
      } catch {
        if (!cancelled) setProductGates([])
      } finally {
        if (!cancelled) setProductGatesLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [tierDialogOpen])

  const canAssignTier = AccessControl.VerifyAccess([
    PERMISSIONS.MEMBER.MEMBER_ASSIGN_TIER,
  ])
  const canAssignProgram = AccessControl.VerifyAccess([
    PERMISSIONS.MEMBER.MEMBER_ASSIGN_PROGRAM,
  ])
  const canRemoveAccess = AccessControl.VerifyAccess([
    PERMISSIONS.MEMBER.MEMBER_REMOVE_ACCESS,
  ])

  if (loading) {
    return (
      <Card className="section-card">
        <CardContent>
          <Typography sx={{ mb: 1.5 }} color="text.secondary">
            <CircularProgress />
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <StyledMemberSubscriptions>
      <div className="action-toolbar">
        {canAssignTier && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => setTierDialogOpen(true)}
            disabled={loading}
          >
            Assign New Tier
          </Button>
        )}
        {canAssignProgram && (
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setProgramDialogOpen(true)}
            disabled={loading}
          >
            Assign Program
          </Button>
        )}
      </div>

      {accessList.length > 0 && (
        <Card className="section-card">
          <CardContent>
            <h3 className="section-title">
              Current Tier Access
            </h3>
            <div className="access-grid">
              {accessList.map((access) => {
                const isProductAccess =
                  String(access.tierType || "").toLowerCase() === "product"
                const productGate = isProductAccess
                  ? getProductGateByAccessValue(access.accessValue)
                  : null
                const productGateId = Number(access.accessValue || 0)

                return (
                  <div key={access.id} className="access-item">
                    <div className="access-header">
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {productGate?.name || access.tierType}
                      </Typography>
                      {canRemoveAccess && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleRemoveAccess(access)}
                          disabled={loading}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                    <p className="access-meta">
                      Granted: {moment(access.grantedAt).format("LL")}
                    </p>
                    {access.trialStartAt && access.trialEndAt && (
                      <p className="access-meta">
                        Trial: {moment(access.trialStartAt).format("LL")} - {moment(access.trialEndAt).format("LL")}
                      </p>
                    )}
                    <p className="access-meta">
                      Trial Used: {access.trialUsed ? "Yes" : "No"}
                    </p>
                    {isProductAccess && (
                      <div className="program-detail-box">
                        <p className="program-detail-title">
                          Product Gate:
                        </p>
                        {productGate ? (
                          <Box>
                            <p className="program-detail-item">
                              <strong>Name:</strong> {productGate.name}
                            </p>
                            {productGate.code && (
                              <p className="program-detail-item">
                                <strong>Shortcode:</strong> {productGate.code}
                              </p>
                            )}
                            {productGate.checkoutUrl && (
                              <p className="program-detail-item">
                                <strong>Checkout URL:</strong> {productGate.checkoutUrl}
                              </p>
                            )}
                          </Box>
                        ) : productGatesLoading ? (
                          <Typography variant="body2" color="text.secondary">
                            Loading product gate...
                          </Typography>
                        ) : productGateId > 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Product gate #{productGateId} is not available in the product gate list.
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Product gate details not available
                          </Typography>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscription Information */}
      {subscriptions.length === 0 ? (
        <Card className="section-card">
          <CardContent>
            <Typography sx={{ mb: 1.5 }} color="text.secondary">
              No subscription yet.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        subscriptions.map((subscription, index) => {
          const product = getProductByCode(
            subscription.appleProductId || subscription.productCode
          )
          return (
            <div
              className="review"
              key={`subscription-item-${subscription.id}`}
            >
              <Card className="section-card">
                <CardContent>
                  <Typography
                    sx={{ fontSize: 14 }}
                    color="text.secondary"
                    gutterBottom
                  >
                    Subscription Order Number:
                  </Typography>
                  <Typography variant="h5" component="div">
                    {subscription.orderNo}
                  </Typography>

                  {/* Add Subscription Status Control Here */}
                  {payment === PAYMENT_TYPE_INDEX.INFUSION_SOFT &&
                    index === 0 && (
                      <div>
                        <Button
                          className="can-btn"
                          variant="contained"
                          color="error"
                          onClick={() => {
                            subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVATE
                              ? cancelSubscription()
                              : reactivateSubscription()
                          }}
                        >
                          {subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVATE
                            ? "Cancel Subscription"
                            : "Reactivated Subscription"}
                        </Button>
                      </div>
                    )}

                  <Box sx={{ flexGrow: 0.5 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Grid item xs={4} md={6}>
                          <Typography
                            sx={{ fontSize: 14, mb: 1.5, marginTop: 3 }}
                            color="text.secondary"
                            gutterBottom
                          >
                            Product:
                          </Typography>
                          <Typography variant="body2">
                            {product &&
                              product.groupName &&
                              product.name &&
                              `${product.groupName} ${product.name}`}
                          </Typography>
                        </Grid>

                        <Grid item xs={4} md={6}>
                          <Typography
                            sx={{ fontSize: 14, mb: 1.5, marginTop: 3 }}
                            color="text.secondary"
                            gutterBottom
                          >
                            Duration:
                          </Typography>
                          <Typography variant="body2">
                            {`${subscription.duration} ${
                              DURATION_UNIT_LIST.find(
                                (o) => o.value === subscription.durationUnit
                              ).text
                            }`}
                          </Typography>
                        </Grid>
                        {subscription.payment !== 0 &&
                          subscription.payment !== 2 &&
                          subscription.orderStatus === 1 && (
                            <Grid item xs={6}>
                              <Typography
                                sx={{ fontSize: 14, mb: 1.5, marginTop: 3 }}
                                color="text.secondary"
                                gutterBottom
                              >
                                Transaction ID:
                              </Typography>
                              <Typography variant="body2">
                                {subscription.transactionId}
                              </Typography>
                            </Grid>
                          )}

                        {subscription.orderStatus === 1 && (
                          <Grid item xs={6}>
                            <Typography
                              sx={{ fontSize: 14, mb: 1.5, marginTop: 3 }}
                              color="text.secondary"
                              gutterBottom
                            >
                              Effect Date:
                            </Typography>
                            <Typography variant="body2">
                              {`${moment(subscription.thirdStartAt).format(
                                "LL"
                              )} to ${moment(subscription.thirdEndAt).format(
                                "LL"
                              )}
                              `}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>

                      <Grid item xs={4} md={6}>
                        {subscription.orderStatus === 0 && (
                          <Grid item xs={6}>
                            <Typography
                              sx={{ fontSize: 14, mb: 1.5, marginTop: 3 }}
                              color="text.secondary"
                              gutterBottom
                            >
                              Order Date:
                            </Typography>
                            <Typography variant="body2">
                              {moment(subscription.createdAtStamp).format(
                                "LLL"
                              )}
                            </Typography>
                          </Grid>
                        )}

                        {subscription.orderStatus === 1 && (
                          <Grid item xs={6}>
                            <Typography
                              sx={{ fontSize: 14, mb: 1.5, marginTop: 3 }}
                              color="text.secondary"
                              gutterBottom
                            >
                              Order Paid At:
                            </Typography>
                            <Typography variant="body2">
                              {moment(subscription.payAtStamp).format("LLL")}
                            </Typography>
                          </Grid>
                        )}

                        <Grid item xs={6}>
                          <Typography
                            sx={{ fontSize: 14, mb: 1.5, marginTop: 3 }}
                            color="text.secondary"
                            gutterBottom
                          >
                            Order Status:
                          </Typography>
                          <Typography variant="body2">
                            {ORDER_STATUS[subscription.orderStatus]}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            sx={{ fontSize: 14, mb: 1.5, marginTop: 3 }}
                            color="text.secondary"
                            gutterBottom
                          >
                            Payment:
                          </Typography>
                          <Typography variant="body2">
                            {subscription.promotionDetailType !== 1 ? (
                              <>
                                {`${subscription.orderCurrency.toUpperCase()} ${
                                  subscription.orderPrice
                                }`}
                                <br />
                                {subscription.orderStatus === 1 &&
                                  `Payment from ${
                                    PAYMENT_TYPE[subscription.payment]
                                  }`}
                              </>
                            ) : (
                              <>Free Trial</>
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </div>
          )
        })
      )}

      {/* Assign Tier Dialog */}
      <Dialog
        open={tierDialogOpen}
        onClose={() => setTierDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="dialog-title-group">
            <h2 className="dialog-title">Assign New Tier</h2>
            <p className="dialog-subtitle">
              Grant tier access and optional trial duration.
            </p>
          </div>
        </DialogTitle>
        <form onSubmit={handleSubmitTier(handleAssignTier)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tier Type</InputLabel>
                  <Select
                    {...registerTier("tierType", { required: "Tier type is required" })}
                    error={!!errorsTier.tierType}
                    label="Tier Type"
                    size="small"
                    disabled={tierProgramsLoading || !!tierProgramsError}
                    onChange={(e) => {
                      // keep react-hook-form in sync and clear product gate if switching away from product
                      const v = e.target.value
                      setValueTier("tierType", v, { shouldValidate: true })
                      if (String(v || "").toLowerCase() !== "product") {
                        setValueTier("programId", "", { shouldValidate: true })
                      }
                    }}
                  >
                    {tierProgramsLoading ? (
                      <MenuItem disabled>Loading tiers...</MenuItem>
                    ) : tierProgramsError ? (
                      <MenuItem disabled>Failed to load tiers</MenuItem>
                    ) : (
                      tierPrograms.map((tier) => (
                        <MenuItem key={tier.id} value={tier.type}>
                          {tier.type.charAt(0).toUpperCase() + tier.type.slice(1)}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errorsTier.tierType && (
                    <Typography color="error" variant="caption">
                      {errorsTier.tierType.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {String(watchTier("tierType") || "").toLowerCase() === "product" && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Product Gate</InputLabel>
                    <Select
                      label="Product Gate"
                      size="small"
                      disabled={productGatesLoading}
                      {...registerTier("programId", {
                        validate: (v) =>
                          String(watchTier("tierType") || "").toLowerCase() !== "product" ||
                          (!!v || "Product Gate is required"),
                      })}
                      error={!!errorsTier.programId}
                    >
                      {productGatesLoading ? (
                        <MenuItem disabled>Loading product gates...</MenuItem>
                      ) : productGates.length === 0 ? (
                        <MenuItem disabled>No product gates found</MenuItem>
                      ) : (
                        productGates.map((p) => (
                          <MenuItem key={p.programId} value={p.programId}>
                            {p.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {errorsTier.programId && (
                      <Typography color="error" variant="caption">
                        {errorsTier.programId.message}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Duration"
                  type="number"
                  variant="outlined"
                  size="small"
                  {...registerTier("trialDays", {
                    min: { value: 0, message: "Trial days must be 0 or greater" },
                  })}
                  error={!!errorsTier.trialDays}
                  helperText={errorsTier.trialDays?.message}
                  placeholder="Duration"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              className="dialog-cancel-btn"
              variant="outlined"
              size="medium"
              onClick={() => setTierDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="dialog-submit-btn"
              type="submit"
              variant="contained"
              size="medium"
              disabled={loading}
            >
              Assign Tier
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Assign Program Dialog */}
      <Dialog
        open={programDialogOpen}
        onClose={() => setProgramDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <div className="dialog-title-group">
            <h2 className="dialog-title">Assign Program to Member</h2>
            <p className="dialog-subtitle">
              Pick a program and assign it as direct member access.
            </p>
          </div>
        </DialogTitle>
        <form onSubmit={handleSubmitProgram(handleAssignProgram)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  options={programs}
                  value={selectedProgram}
                  getOptionLabel={(option) => option.title || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(event, newValue) => {
                    setSelectedProgram(newValue)
                    // Update the form value using setValue
                    const programId = newValue ? newValue.id : ""
                    setValue("programId", programId, { shouldValidate: true })
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Program"
                      required
                      size="small"
                      error={!!errorsProgram.programId}
                      helperText={errorsProgram.programId?.message}
                    />
                  )}
                  noOptionsText="No programs found"
                  loading={loading}
                />
                {/* Hidden input to properly register the field */}
                <input
                  type="hidden"
                  {...registerProgram("programId", { required: "Program is required" })}
                />
              </Grid>


            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              className="dialog-cancel-btn"
              variant="outlined"
              size="medium"
              onClick={() => {
                setProgramDialogOpen(false)
                setSelectedProgram(null)
                resetProgram()
              }}
            >
              Cancel
            </Button>
            <Button
              className="dialog-submit-btn"
              type="submit"
              variant="contained"
              size="medium"
              disabled={loading}
            >
              Assign Program
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {ConfirmDialog}
    </StyledMemberSubscriptions>
  )
}

export default MemberSubscriptions
